import { useEffect, useRef, type CSSProperties } from 'react'
import { Outlet } from 'react-router-dom'

/**
 * AuthLayout — cinematic stage for login.
 * Drifting orbs (purple + gold) animate continuously across the viewport.
 * A rotating conic aura sits behind the logo. Everything follows the cursor with lag.
 */
export function AuthLayout() {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const handleMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      const nx = (e.clientX - rect.left) / rect.width - 0.5
      const ny = (e.clientY - rect.top) / rect.height - 0.5
      el.style.setProperty('--glow-x', nx.toString())
      el.style.setProperty('--glow-y', ny.toString())
    }
    window.addEventListener('mousemove', handleMove)
    return () => window.removeEventListener('mousemove', handleMove)
  }, [])

  const softGlowStyle: CSSProperties = {
    transform:
      'translate(calc(-50% + (var(--glow-x, 0) * 80px)), calc(-50% + (var(--glow-y, 0) * 80px)))',
  }
  const hotGlowStyle: CSSProperties = {
    transform:
      'translate(calc(-50% + (var(--glow-x, 0) * 140px)), calc(-50% + (var(--glow-y, 0) * 140px)))',
  }

  return (
    <div
      ref={rootRef}
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-4"
    >
      {/* ─── Ambient drifting orbs — full viewport ──────────────────── */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Purple orb A — biggest, slowest */}
        <div className="absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 animate-orb-a">
          <div className="h-full w-full rounded-full bg-primary/20 blur-3xl" />
        </div>
        {/* Purple orb B — mid */}
        <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 animate-orb-b">
          <div className="h-full w-full rounded-full bg-primary/25 blur-3xl" />
        </div>
        {/* Purple orb C — small/fast */}
        <div className="absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 animate-orb-c">
          <div className="h-full w-full rounded-full bg-[#B57CFF]/25 blur-3xl" />
        </div>
        {/* Gold accent orb — rare, distant */}
        <div className="absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 animate-orb-gold">
          <div className="h-full w-full rounded-full bg-accent-gold/20 blur-3xl" />
        </div>
      </div>

      {/* ─── Mouse-tracked halo above logo ───────────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[22%] transition-transform duration-[700ms] ease-out"
        style={softGlowStyle}
      >
        {/* Rotating conic aura — softened */}
        <div className="relative h-[560px] w-[560px]">
          <div
            className="absolute inset-0 animate-aura-rotate opacity-50"
            style={{
              background:
                'conic-gradient(from 0deg, rgba(162,89,255,0) 0%, rgba(162,89,255,0.22) 20%, rgba(240,165,0,0.14) 45%, rgba(162,89,255,0.26) 70%, rgba(162,89,255,0) 100%)',
              filter: 'blur(64px)',
              borderRadius: '9999px',
            }}
          />
        </div>
      </div>

      {/* Hot center — smaller, softer, positioned above the logo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[18%] transition-transform duration-[400ms] ease-out"
        style={hotGlowStyle}
      >
        <div className="h-[260px] w-[260px] rounded-full bg-primary/15 blur-3xl animate-glow-pulse" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Hero Logo */}
        <div className="mb-10 flex justify-center animate-logo-entrance">
          <img
            src="/logo-wordmark-dark.png"
            alt="FreighAI"
            className="block h-28 w-auto object-contain dark:hidden"
          />
          <img
            src="/logo-wordmark-light.png"
            alt="FreighAI"
            className="hidden h-28 w-auto object-contain dark:block"
          />
        </div>

        {/* Auth form — enters after the logo lands */}
        <div className="animate-fade-up [animation-delay:500ms]">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
